/**
 * SurfaceGlyphs — instanced flat glyph quads laid tangent to the globe.
 *
 * Like GlyphMarkers, but surface-oriented instead of camera-facing: each point
 * is a small quad rotated into the sphere's tangent plane (the same trick Pops
 * uses for its triangles), so the glyph looks painted onto the globe and
 * foreshortens at the limb rather than billboarding toward the camera. Pass a
 * `makeTexture` factory to stamp a procedural glyph; omit it for plain square
 * dots. One InstancedMesh = one draw call. See pipeline/ixps.py.
 */

import { useLayoutEffect, useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  DoubleSide,
  type InstancedMesh as TInstancedMesh,
  Matrix4,
  MeshBasicMaterial,
  NormalBlending,
  PlaneGeometry,
  type Texture,
  Vector3,
} from 'three';

import { latLonToVec3 } from './coords.js';

export interface MarkerPoint {
  readonly lat: number;
  readonly lon: number;
}

/** Cloud glyph: a simple puffy cloud silhouette (white on transparent). */
export function makeCloudTexture(): Texture {
  const s = 64;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, s, s);
    ctx.fillStyle = '#ffffff';
    const puffs: ReadonlyArray<readonly [number, number, number]> = [
      [0.3, 0.58, 0.16], // left
      [0.7, 0.58, 0.16], // right
      [0.42, 0.44, 0.18], // top-left bump
      [0.6, 0.46, 0.16], // top-right bump
      [0.5, 0.6, 0.2], // body
    ];
    ctx.beginPath();
    for (const [cx, cy, r] of puffs) {
      ctx.moveTo((cx + r) * s, cy * s);
      ctx.arc(cx * s, cy * s, r * s, 0, Math.PI * 2);
    }
    ctx.rect(0.3 * s, 0.58 * s, 0.4 * s, 0.16 * s); // flat-ish base
    ctx.fill();
  }
  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

/** Triangle glyph: a filled upward-pointing triangle (white on transparent). */
export function makeTriangleTexture(): Texture {
  const s = 64;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, s, s);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(0.5 * s, 0.12 * s); // top tip
    ctx.lineTo(0.87 * s, 0.82 * s); // bottom-right
    ctx.lineTo(0.13 * s, 0.82 * s); // bottom-left
    ctx.closePath();
    ctx.fill();
  }
  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

export interface SurfaceGlyphsProps {
  readonly points: readonly MarkerPoint[];
  /** Globe radius the markers sit on. Default 1. */
  readonly radius?: number;
  /** Quad width in world units. Default 0.03. */
  readonly size?: number;
  readonly color?: string | number;
  readonly opacity?: number;
  /** Surface-lift multiplier; higher floats the layer further out. Default 1.01. */
  readonly lift?: number;
  /** Optional procedural glyph texture. Omit for plain square quads. */
  readonly makeTexture?: () => Texture;
  /** Additive blending for a glow (like Pops), or normal blending. Default false. */
  readonly additive?: boolean;
  /** Transparent-sort order; lower draws first (further back). Default 0. */
  readonly renderOrder?: number;
}

export function SurfaceGlyphs({
  points,
  radius = 1,
  size = 0.03,
  color = 0xffffff,
  opacity = 0.9,
  lift = 1.01,
  makeTexture,
  additive = false,
  renderOrder = 0,
}: SurfaceGlyphsProps): JSX.Element | null {
  const meshRef = useRef<TInstancedMesh>(null);
  const count = points.length;

  // Unit quad in the local XY plane, facing +Z; the per-instance matrix below
  // scales and rotates it onto the surface.
  const geometry = useMemo(() => new PlaneGeometry(1, 1), []);

  const material = useMemo(() => {
    const map = makeTexture ? makeTexture() : null;
    return new MeshBasicMaterial({
      ...(map ? { map, alphaTest: 0.05 } : {}),
      color: new Color(color),
      transparent: true,
      opacity,
      depthWrite: false,
      side: DoubleSide,
      blending: additive ? AdditiveBlending : NormalBlending,
    });
    // makeTexture is a stable factory; intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, opacity, additive]);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const matrix = new Matrix4();
    const pos = new Vector3();
    // Reused basis vectors: w = outward normal, (u, v) span the tangent plane.
    const w = new Vector3();
    const u = new Vector3();
    const v = new Vector3();
    const up = new Vector3();
    const worldY = new Vector3(0, 1, 0);
    const worldX = new Vector3(1, 0, 0);

    for (let i = 0; i < count; i++) {
      const p = points[i]!;
      pos.copy(latLonToVec3(p.lat, p.lon, radius * lift));
      // Orient the quad flat on the surface: +Z → outward normal, +Y → north.
      // Near the poles fall back to a world-X up reference.
      w.copy(pos).normalize();
      up.copy(Math.abs(w.y) > 0.95 ? worldX : worldY);
      u.crossVectors(up, w).normalize();
      v.crossVectors(w, u);
      // Row-major: columns are the scaled basis (u, v, w) and the translation.
      matrix.set(
        u.x * size, v.x * size, w.x * size, pos.x,
        u.y * size, v.y * size, w.y * size, pos.y,
        u.z * size, v.z * size, w.z * size, pos.z,
        0, 0, 0, 1,
      );
      mesh.setMatrixAt(i, matrix);
    }

    mesh.count = count;
    mesh.instanceMatrix.needsUpdate = true;
  }, [points, count, radius, size, lift]);

  if (count === 0) return null;
  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, Math.max(count, 1)]}
      renderOrder={renderOrder}
    />
  );
}
